class LotrTimelineSection {
  static SECTION_SELECTOR = '[data-lotr-timeline-section]';
  static PROGRESS_SELECTOR = '.lotr-timeline-section__progress';
  static CARD_SELECTOR = '[data-lotr-timeline-card]';
  static NODE_SELECTOR = '[data-lotr-timeline-node]';
  static CONNECTOR_SELECTOR = '[data-lotr-timeline-connector]';
  static EQUAL_HEIGHT_SELECTORS = [
    '.lotr-timeline-section__stage-label',
    '.lotr-timeline-section__card-title',
    '.lotr-timeline-section__card-description',
  ];
  static MAX_SCHEDULE_DELAY_MS = 2147483647;
  static DEFAULT_TIMEZONE_OFFSET = -8;
  static ACTIVATION_BASIS_VISITOR = 'visitor_local';
  static DATETIME_PATTERN = /(\d{4})-(\d{2})-(\d{2})[\sT](\d{2}):(\d{2}):(\d{2})/;
  static DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
  static visibilityRefreshBound = false;

  constructor(section) {
    this.section = section;
    this.resizeHandler = null;
    this.resizeFrame = null;
    this.realtimeTimeout = null;
    this.realtimeInterval = null;
    this.nextActivationTime = null;
  }

  /**
   * 解析时区偏移（与 valentine-timeline.js 一致）
   * 支持: "UTC-7", "UTC+8", "-7", "+8"
   */
  static parseTimezoneOffset(str) {
    if (!str) return LotrTimelineSection.DEFAULT_TIMEZONE_OFFSET;

    const cleaned = String(str).replace(/UTC/i, '').trim();
    const offset = Number.parseFloat(cleaned);

    if (Number.isNaN(offset) || offset < -12 || offset > 14) {
      return LotrTimelineSection.DEFAULT_TIMEZONE_OFFSET;
    }

    return offset;
  }

  /**
   * 将日期时间字符串解析为指定 UTC 偏移下的瞬间（毫秒）
   * 支持: YYYY-MM-DD HH:MM:SS / YYYY-MM-DDTHH:MM:SS
   */
  static parseTimeWithOffset(timeString, utcOffset) {
    if (!timeString) return null;

    try {
      const normalized = String(timeString).trim();
      if (!normalized) return null;

      if (normalized.includes('Z') || /[+-]\d{2}:\d{2}$/.test(normalized)) {
        const parsed = new Date(normalized);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      }

      const match = normalized.match(/(\d{4})-(\d{2})-(\d{2})[\sT](\d{2}):(\d{2}):(\d{2})/);
      if (!match) return null;

      const [, year, month, day, hour, minute, second] = match;
      const localTime = Date.UTC(
        Number.parseInt(year, 10),
        Number.parseInt(month, 10) - 1,
        Number.parseInt(day, 10),
        Number.parseInt(hour, 10),
        Number.parseInt(minute, 10),
        Number.parseInt(second, 10),
      );
      const utcTime = localTime - utcOffset * 60 * 60 * 1000;

      return new Date(utcTime);
    } catch {
      return null;
    }
  }

  static parseDateTimeParts(match) {
    return {
      year: Number.parseInt(match[1], 10),
      month: Number.parseInt(match[2], 10) - 1,
      day: Number.parseInt(match[3], 10),
      hour: Number.parseInt(match[4], 10),
      minute: Number.parseInt(match[5], 10),
      second: Number.parseInt(match[6], 10),
    };
  }

  /**
   * 按访客浏览器本地时区解析（各地日历上的同一时刻）
   */
  static parseTimeAsVisitorLocal(timeString) {
    const normalized = String(timeString).trim();
    if (!normalized) return null;

    if (normalized.includes('Z') || /[+-]\d{2}:\d{2}$/.test(normalized)) {
      const parsed = new Date(normalized);
      return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
    }

    const datetimeMatch = normalized.match(LotrTimelineSection.DATETIME_PATTERN);
    if (datetimeMatch) {
      const parts = LotrTimelineSection.parseDateTimeParts(datetimeMatch);
      const parsed = new Date(parts.year, parts.month, parts.day, parts.hour, parts.minute, parts.second);
      return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
    }

    const dateOnlyMatch = normalized.match(LotrTimelineSection.DATE_ONLY_PATTERN);
    if (dateOnlyMatch) {
      const parsed = new Date(
        Number.parseInt(dateOnlyMatch[1], 10),
        Number.parseInt(dateOnlyMatch[2], 10) - 1,
        Number.parseInt(dateOnlyMatch[3], 10),
        0,
        0,
        0,
      );
      return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
    }

    return null;
  }

  getTimezoneOffset() {
    return LotrTimelineSection.parseTimezoneOffset(this.section.dataset.timezoneOffset);
  }

  usesVisitorLocalBasis() {
    return this.section.dataset.activationBasis === LotrTimelineSection.ACTIVATION_BASIS_VISITOR;
  }

  parseActivationDatetime(timeString) {
    const normalized = String(timeString).trim();
    if (!normalized) return null;

    if (this.usesVisitorLocalBasis()) {
      return LotrTimelineSection.parseTimeAsVisitorLocal(normalized);
    }

    const parsed = LotrTimelineSection.parseTimeWithOffset(normalized, this.getTimezoneOffset());
    return parsed && !Number.isNaN(parsed.getTime()) ? parsed.getTime() : null;
  }

  getCardActivationTime(card) {
    const datetime = card.dataset.activationDatetime?.trim();
    if (!datetime) return null;

    return this.parseActivationDatetime(datetime);
  }

  static getIndex(element) {
    const index = Number.parseInt(element.dataset.index, 10);
    return Number.isNaN(index) ? -1 : index;
  }

  static setActiveState(elements, activeIndex, className) {
    elements.forEach((element) => {
      const index = LotrTimelineSection.getIndex(element);
      element.classList.toggle(className, index > -1 && index <= activeIndex);
    });
  }

  update() {
    const { section } = this;
    const progressRoot = section.querySelector(LotrTimelineSection.PROGRESS_SELECTOR);
    const cards = Array.from(section.querySelectorAll(LotrTimelineSection.CARD_SELECTOR));
    const nodes = progressRoot ? Array.from(progressRoot.querySelectorAll(LotrTimelineSection.NODE_SELECTOR)) : [];
    const connectors = progressRoot
      ? Array.from(progressRoot.querySelectorAll(LotrTimelineSection.CONNECTOR_SELECTOR))
      : [];
    const now = Date.now();
    let activeIndex = -1;

    cards.forEach((card) => {
      const index = LotrTimelineSection.getIndex(card);
      const activationTime = this.getCardActivationTime(card);
      const forceActive = card.dataset.forceActive === 'true';

      if (index > -1 && (forceActive || (activationTime !== null && now >= activationTime))) {
        activeIndex = Math.max(activeIndex, index);
      }
    });

    LotrTimelineSection.setActiveState(cards, activeIndex, 'is-active');
    LotrTimelineSection.setActiveState(nodes, activeIndex, 'is-active');

    connectors.forEach((connector) => {
      const index = LotrTimelineSection.getIndex(connector);
      connector.classList.toggle('is-connector-active', index > -1 && index <= activeIndex);
    });

    section.classList.add('is-initialized');
  }

  equalizeTextHeights() {
    const { section } = this;

    LotrTimelineSection.EQUAL_HEIGHT_SELECTORS.forEach((selector) => {
      const elements = Array.from(section.querySelectorAll(selector));
      if (!elements.length) return;

      elements.forEach((element) => {
        element.style.minHeight = '';
      });

      const maxHeight = elements.reduce((height, element) => Math.max(height, element.offsetHeight), 0);
      if (!maxHeight) return;

      elements.forEach((element) => {
        element.style.minHeight = `${maxHeight}px`;
      });
    });
  }

  bindResizeEqualizer() {
    if (this.resizeHandler) return;

    this.resizeHandler = () => {
      window.cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = window.requestAnimationFrame(() => this.equalizeTextHeights());
    };

    window.addEventListener('resize', this.resizeHandler);
  }

  unbindResizeEqualizer() {
    if (!this.resizeHandler) return;

    window.removeEventListener('resize', this.resizeHandler);
    window.cancelAnimationFrame(this.resizeFrame);
    this.resizeHandler = null;
    this.resizeFrame = null;
  }

  collectFutureActivationTimes() {
    const now = Date.now();
    const times = new Set();

    this.section.querySelectorAll(LotrTimelineSection.CARD_SELECTOR).forEach((card) => {
      if (card.dataset.forceActive === 'true') return;

      const activationTime = this.getCardActivationTime(card);
      if (activationTime !== null && activationTime > now) {
        times.add(activationTime);
      }
    });

    return Array.from(times).sort((left, right) => left - right);
  }

  stopRealtimeActivation() {
    if (this.realtimeTimeout) {
      window.clearTimeout(this.realtimeTimeout);
      this.realtimeTimeout = null;
    }

    if (this.realtimeInterval) {
      window.clearInterval(this.realtimeInterval);
      this.realtimeInterval = null;
    }

    this.nextActivationTime = null;
  }

  scheduleNextActivation() {
    const { section } = this;

    if (section.dataset.realtimeActivationEnabled !== 'true') {
      this.stopRealtimeActivation();
      return;
    }

    this.stopRealtimeActivation();

    const futureTimes = this.collectFutureActivationTimes();
    if (!futureTimes.length) return;

    const nextTime = futureTimes[0];
    this.nextActivationTime = nextTime;

    const delay = nextTime - Date.now();
    const wait = Math.min(Math.max(0, delay), LotrTimelineSection.MAX_SCHEDULE_DELAY_MS);

    this.realtimeTimeout = window.setTimeout(() => {
      this.realtimeTimeout = null;

      if (Date.now() < nextTime) {
        this.scheduleNextActivation();
        return;
      }

      this.update();
      this.scheduleNextActivation();
    }, wait);
  }

  startRealtimeActivation() {
    if (this.section.dataset.realtimeActivationEnabled !== 'true') return;

    this.scheduleNextActivation();
  }

  init() {
    const { section } = this;
    if (!section || section.dataset.lotrTimelineInitialized === 'true') return;

    this.update();
    this.equalizeTextHeights();
    document.fonts?.ready?.then(() => this.equalizeTextHeights());
    this.bindResizeEqualizer();
    this.startRealtimeActivation();

    section.dataset.lotrTimelineInitialized = 'true';
    section._lotrTimelineInstance = this;
  }

  destroy() {
    this.stopRealtimeActivation();
    this.unbindResizeEqualizer();

    delete this.section._lotrTimelineInstance;
    delete this.section.dataset.lotrTimelineInitialized;
  }

  static getSections(root = document) {
    const sections = [];

    if (root.matches?.(LotrTimelineSection.SECTION_SELECTOR)) {
      sections.push(root);
    }

    root.querySelectorAll?.(LotrTimelineSection.SECTION_SELECTOR).forEach((section) => sections.push(section));
    return sections;
  }

  static initializeAll(root = document) {
    LotrTimelineSection.bindVisibilityRefresh();
    LotrTimelineSection.getSections(root).forEach((section) => {
      new LotrTimelineSection(section).init();
    });
  }

  static destroyAllIn(root) {
    LotrTimelineSection.getSections(root).forEach((section) => {
      section._lotrTimelineInstance?.destroy();
    });
  }

  static bindVisibilityRefresh() {
    if (LotrTimelineSection.visibilityRefreshBound) return;

    LotrTimelineSection.visibilityRefreshBound = true;

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return;

      LotrTimelineSection.getSections().forEach((section) => {
        if (section.dataset.realtimeActivationEnabled !== 'true') return;

        const instance = section._lotrTimelineInstance;
        if (!instance) return;

        instance.update();
        instance.scheduleNextActivation();
      });
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => LotrTimelineSection.initializeAll());
} else {
  LotrTimelineSection.initializeAll();
}

document.addEventListener('shopify:section:load', (event) => {
  LotrTimelineSection.initializeAll(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  LotrTimelineSection.destroyAllIn(event.target);
});
